import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';

const PrivateAdminRoute = ({ children }) => {
  // This route is already nested inside the app-wide <PrivateRoute>
  // (routes.js), which reliably gates on `isAuthenticated` and shows its own
  // loading spinner until the initial auth check resolves -- so by the time
  // this component renders at all, the user is already known to be
  // authenticated. The previous version re-implemented its own "is the user
  // loaded yet" check using `user === undefined`, but authslice.js's real
  // initial/interim value is `null`, never `undefined` -- so that check was
  // always false, and a route change here could render before `user` was
  // (re)populated, incorrectly bouncing an already-logged-in admin to
  // /login. That bounce-then-immediately-redirected-back is exactly what
  // looked like "clicking just refreshes the page." Using the same
  // `isAuthenticated` flag PrivateRoute already relies on removes the
  // fragile object-truthiness check entirely.
  const { user, loading, isAuthenticated } = useSelector((state) => state.auth);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-2 text-gray-600">Checking admin access...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check admin permissions with better error handling
  const isAdmin = user?.is_admin === true || user?.role === 'admin';
  // Company Ops staff (occupies an org role -- see UserOrgRole/staff_required
  // on the backend) get into /admin too, but adminDashboard.jsx itself
  // restricts which tabs they see via GET /admin/access -- this route guard
  // only decides "can this person be here at all," not "see everything."
  const isStaff = user?.is_staff === true;
  if (!isAdmin && !isStaff) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

export default PrivateAdminRoute;